/**
 * FleetMap — controllers de mapa (fonte única).
 * Extraídos fielmente de DashboardPage (ADR-2026-05-29-shared-fleetmap-component).
 *
 * - FitBoundsController: centraliza na seleção (sidebar) e no "Ver Todos"; fit inicial.
 * - FollowController: modo Seguir (panTo preservando zoom; arrastar desengata).
 * - TrailHoverController: crosshair de hover na trilha (apresentação; snap em px).
 */

import { useEffect, useRef, useCallback } from 'react';
import { useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

const TRAIL_HOVER_SNAP_PX = 18;
const TRAIL_HOVER_FRAME_MS = 50;

export const FitBoundsController = ({ equipments, selectedEquipment, shouldFitBounds, selectedFromSidebar, onFitComplete }) => {
  const map = useMap();
  const lastSelectedIdRef = useRef(null);
  const hasFittedRef = useRef(false);

  useEffect(() => {
    if (!selectedFromSidebar) return;
    if (!selectedEquipment || !selectedEquipment.latitude || !selectedEquipment.longitude) return;
    if (lastSelectedIdRef.current !== selectedEquipment.id) {
      map.setView([selectedEquipment.latitude, selectedEquipment.longitude], 14);
      lastSelectedIdRef.current = selectedEquipment.id;
    }
  }, [selectedEquipment, selectedFromSidebar, map]);

  useEffect(() => {
    if (!shouldFitBounds) return;
    if (!equipments || equipments.length === 0) return;
    const eqsWithLoc = equipments.filter(eq => eq.latitude && eq.longitude);
    if (eqsWithLoc.length === 0) return;
    if (eqsWithLoc.length === 1) {
      map.setView([eqsWithLoc[0].latitude, eqsWithLoc[0].longitude], 12);
    } else {
      map.fitBounds(L.latLngBounds(eqsWithLoc.map(eq => [eq.latitude, eq.longitude])), { padding: [50, 50], maxZoom: 14 });
    }
    hasFittedRef.current = true;
    if (onFitComplete) onFitComplete();
  }, [shouldFitBounds, equipments, map, onFitComplete]);

  useEffect(() => {
    if (hasFittedRef.current) return;
    if (!equipments || equipments.length === 0) return;
    const eqsWithLoc = equipments.filter(eq => eq.latitude && eq.longitude);
    if (eqsWithLoc.length === 0) return;
    if (eqsWithLoc.length === 1) {
      map.setView([eqsWithLoc[0].latitude, eqsWithLoc[0].longitude], 12);
    } else {
      map.fitBounds(L.latLngBounds(eqsWithLoc.map(eq => [eq.latitude, eq.longitude])), { padding: [50, 50], maxZoom: 14 });
    }
    hasFittedRef.current = true;
  }, [equipments, map]);

  useEffect(() => {
    if (!selectedEquipment) lastSelectedIdRef.current = null;
  }, [selectedEquipment]);

  return null;
};

export const FollowController = ({ equipments, followedIds, onDisengage }) => {
  const map = useMap();
  const framedKeyRef = useRef(null);
  const lastCenterRef = useRef(null);

  useEffect(() => {
    const handleDragStart = () => onDisengage();
    map.on('dragstart', handleDragStart);
    return () => map.off('dragstart', handleDragStart);
  }, [map, onDisengage]);

  useEffect(() => {
    if (!followedIds || followedIds.size === 0) {
      framedKeyRef.current = null;
      lastCenterRef.current = null;
      return;
    }
    const points = equipments
      .filter(eq => followedIds.has(eq.id) && eq.latitude && eq.longitude)
      .map(eq => [eq.latitude, eq.longitude]);
    if (points.length === 0) return;

    const key = Array.from(followedIds).sort().join(',');
    const bounds = L.latLngBounds(points);
    const center = bounds.getCenter();
    const centerKey = `${center.lat.toFixed(6)},${center.lng.toFixed(6)}`;

    if (framedKeyRef.current !== key) {
      if (points.length === 1) {
        map.setView(points[0], 14);
      } else {
        map.fitBounds(bounds, { padding: [60, 60], maxZoom: 15 });
      }
      framedKeyRef.current = key;
      lastCenterRef.current = centerKey;
    } else if (lastCenterRef.current !== centerKey) {
      lastCenterRef.current = centerKey;
      map.panTo(center, { animate: true, duration: 0.5 });
    }
  }, [equipments, followedIds, map]);

  return null;
};

export const TrailHoverController = ({ enabled, points, onHoverChange, onPointClick }) => {
  const map = useMap();
  const pointsRef = useRef(points || []);
  const rafRef = useRef(null);
  const pendingLatLngRef = useRef(null);
  const lastResolveRef = useRef(0);
  const hoveredKeyRef = useRef('');
  const hoveredPointRef = useRef(null);

  useEffect(() => { pointsRef.current = points || []; }, [points]);

  const clearHover = useCallback(() => {
    hoveredKeyRef.current = '';
    hoveredPointRef.current = null;
    onHoverChange(null);
    map.getContainer().style.cursor = '';
  }, [map, onHoverChange]);

  const resolveNearest = useCallback((latlng) => {
    const activePoints = pointsRef.current || [];
    if (!enabled || !latlng || activePoints.length === 0) { clearHover(); return; }
    const mousePoint = map.latLngToContainerPoint(latlng);
    let nearest = null;
    let nearestDistSq = Number.POSITIVE_INFINITY;
    for (let i = 0; i < activePoints.length; i += 1) {
      const p = activePoints[i];
      if (p?.lat == null || p?.lon == null) continue;
      const projected = map.latLngToContainerPoint([p.lat, p.lon]);
      const dx = projected.x - mousePoint.x;
      const dy = projected.y - mousePoint.y;
      const distSq = (dx * dx) + (dy * dy);
      if (distSq < nearestDistSq) { nearestDistSq = distSq; nearest = p; }
    }
    const thresholdSq = TRAIL_HOVER_SNAP_PX * TRAIL_HOVER_SNAP_PX;
    const selectedPoint = nearestDistSq <= thresholdSq ? nearest : null;
    const selectedKey = selectedPoint
      ? `${selectedPoint.equipmentId}:${selectedPoint.time}:${selectedPoint.lat}:${selectedPoint.lon}`
      : '';
    if (selectedKey !== hoveredKeyRef.current) {
      hoveredKeyRef.current = selectedKey;
      hoveredPointRef.current = selectedPoint;
      onHoverChange(selectedPoint);
    }
    map.getContainer().style.cursor = selectedPoint ? 'crosshair' : '';
  }, [clearHover, enabled, map, onHoverChange]);

  useMapEvents({
    mousemove: (event) => {
      if (!enabled) { clearHover(); return; }
      pendingLatLngRef.current = event.latlng;
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const now = Date.now();
        if (now - lastResolveRef.current < TRAIL_HOVER_FRAME_MS) return;
        lastResolveRef.current = now;
        resolveNearest(pendingLatLngRef.current);
      });
    },
    mouseout: () => { clearHover(); },
    click: () => {
      if (!enabled || !hoveredPointRef.current) return;
      onPointClick(hoveredPointRef.current);
    },
  });

  useEffect(() => {
    if (!enabled) { clearHover(); return; }
    hoveredKeyRef.current = '';
    hoveredPointRef.current = null;
    onHoverChange(null);
    map.getContainer().style.cursor = '';
  }, [clearHover, enabled, map, onHoverChange]);

  useEffect(() => () => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  }, []);

  return null;
};
